import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { KeyboardKeyName } from '@hub/models/data';
import { useIsomorphicLayoutEffect } from '@hub/hooks/useIsomorphicLayoutEffect';
import { getAttributeValue } from '@hub/bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';
import { Badge } from '@hub/common/Badge/Badge';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { WidenVideo } from '@hub/common/Widen/WidenVideo';
import 'keen-slider/keen-slider.min.css';

export interface ProductMediaModalCarouselProps {
  activeIndex: number;
  isProductDiscontinued?: boolean;
  isProductNew?: boolean;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
}

export const ProductMediaModalCarousel = ({
  activeIndex,
  isProductDiscontinued,
  isProductNew,
  media,
  onChange,
}: ProductMediaModalCarouselProps) => {
  const { t } = useTranslation(['product/productMediaCarousel', 'product/productDetails']);
  const isMountedRef = useRef<boolean>(false);

  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    loop: true,
    spacing: 0,
    mounted: s => {
      const keyboardListener = (e: KeyboardEvent) => {
        if (e.key === KeyboardKeyName.ARROW_LEFT || e.key === KeyboardKeyName.IE_ARROW_LEFT) {
          s.prev();
        } else if (e.key === KeyboardKeyName.ARROW_RIGHT || e.key === KeyboardKeyName.IE_ARROW_RIGHT) {
          s.next();
        }
      };

      // remove event listener if it has been added before
      window.removeEventListener('keyup', keyboardListener);
      window.addEventListener('keyup', keyboardListener);
    },
    slideChanged: s => {
      if (
        !isMountedRef.current ||
        Number.isNaN(s.details().relativeSlide) ||
        s.details().relativeSlide === activeIndex
      ) {
        return true;
      }
      /* istanbul ignore next */
      onChange(s.details().relativeSlide);
      return undefined;
    },
  });

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const headline = media[activeIndex] ? getAttributeValue<string>(media[activeIndex].heading) : '';

  const description = media[activeIndex]?.description;

  const isDrawing = media[activeIndex].type === MediaElementType.DRAWING;

  return (
    <>
      {headline && <h4 className="w-full top-0 h-32 flex flex-col justify-center">{headline}</h4>}
      <div data-testid="product-image-carousel-modal" className="relative m-auto" style={{ maxWidth: '1024px' }}>
        {isProductNew && <Badge text={t('PRODUCT_DETAILS.NEW_PRODUCT_BADGE', { ns: 'product/productDetails' })} />}
        {isProductDiscontinued && (
          <Badge text={t('PRODUCT_DETAILS.DISCONTINUED_BADGE', { ns: 'product/productDetails' })} />
        )}
        <div className="relative border border-gray-200 m-auto">
          {media.length > 1 && (
            <>
              <div
                data-testid="product-image-carousel-modal-prev"
                className="absolute cursor-pointer top-1/2 -translate-y-1/2 -left-20"
                onClick={() => carousel.prev()}
              >
                <Icon size={16} id={IconID.ARROW_LEFT} color={IconColor.LIGHT_GRAY} />
              </div>
              <div
                data-testid="product-image-carousel-modal-next"
                className="absolute cursor-pointer top-1/2 -translate-y-1/2 -right-20"
                onClick={() => carousel.next()}
              >
                <Icon size={16} id={IconID.ARROW_RIGHT} color={IconColor.LIGHT_GRAY} />
              </div>
            </>
          )}
          <div ref={carouselRef} className="keen-slider">
            {media.map((img, idx) => (
              <div
                className={`keen-slider__slide flex number-slide${idx + 1}`}
                key={`modal-carousel-image-${img.shortCode}`}
              >
                {img.type === MediaElementType.IMAGE || img.type === MediaElementType.DRAWING ? (
                  <WidenImage
                    width={1024}
                    height={680}
                    alt={img.description || img.title}
                    layout="intrinsic"
                    shortCode={img.shortCode}
                    filename={img.filename}
                    title={img.title}
                    priority={img.priority}
                  />
                ) : (
                  <WidenVideo
                    shortCode={img.shortCode}
                    filename={img.filename}
                    posterWidth={640}
                    posterHeight={480}
                    hd
                    title={img.title}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="text-left">
          {description && !isDrawing && (
            <p
              data-testid="product-media-carousel-description"
              className="mt-6"
              dangerouslySetInnerHTML={{ __html: description || '' }}
            />
          )}
          {!isDrawing && (
            <p data-testid="product-media-carousel-similar-info" className="text-xxs mt-6">
              {t('PRODUCT_MEDIA_CAROUSEL.SIMILAR', { ns: 'bearing-hub/bearingHub' })}
            </p>
          )}
        </div>
      </div>
    </>
  );
};
